
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BookOpen, FileCode, File, Sparkles, FileText } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import AIDocumentGenerator from './AIDocumentGenerator';

const DocumentGenerationTab = () => {
  const [documentResult, setDocumentResult] = useState("");
  const { toast } = useToast();

  return (
    <div className="space-y-6">
      {/* AI Document Generation Card */}
      <AIDocumentGenerator onResultGenerated={setDocumentResult} />

      {documentResult && (
        <Dialog>
          <DialogTrigger asChild>
            <Button className="w-full mt-4" variant="outline">
              <FileText className="mr-2 h-4 w-4" />
              View Generated Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Generated Document</DialogTitle>
              <DialogDescription>
                Review and edit the AI-generated document as needed
              </DialogDescription>
            </DialogHeader>
            <div className="border rounded-md p-4 bg-muted/30">
              <pre className="whitespace-pre-wrap font-mono text-sm">{documentResult}</pre>
            </div>
            <DialogFooter className="flex justify-between items-center">
              <Button variant="outline">
                Edit Document
              </Button>
              <div className="flex gap-2">
                <Button variant="outline">
                  <FileText className="mr-2 h-4 w-4" />
                  Download
                </Button>
                <Button>
                  Save to Documents
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              Minutes of Meeting
            </CardTitle>
            <CardDescription>Generate minutes for board and committee meetings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Recent Templates</h4>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">Board Meeting Minutes</span>
                <Button variant="ghost" size="sm">Use</Button>
              </div>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">Audit Committee Minutes</span>
                <Button variant="ghost" size="sm">Use</Button>
              </div>
            </div>
            <Button className="w-full">Create New MoM</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCode className="h-5 w-5 text-primary" />
              Board Resolutions
            </CardTitle>
            <CardDescription>Generate standard and custom board resolutions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Resolution Types</h4>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">Bank Account Opening</span>
                <Button variant="ghost" size="sm">Generate</Button>
              </div>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">Director Appointment</span>
                <Button variant="ghost" size="sm">Generate</Button>
              </div>
            </div>
            <Button className="w-full">Custom Resolution</Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <File className="h-5 w-5 text-primary" />
              E-Forms
            </CardTitle>
            <CardDescription>Auto-generate regulatory e-Forms</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Common Forms</h4>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">DIR-12 (Director Changes)</span>
                <Button variant="ghost" size="sm">Generate</Button>
              </div>
              <div className="border rounded-md p-3 flex items-center justify-between">
                <span className="text-sm">MGT-14 (Resolutions)</span>
                <Button variant="ghost" size="sm">Generate</Button>
              </div>
            </div>
            <Button className="w-full">View All E-Forms</Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DocumentGenerationTab;
